import classNames from 'classnames'

import React, {forwardRef} from 'react'
import AutoLink from '../AutoLink'

import '../../../scss/components/_button.scss'

export enum ButtonColor {
  default = 'default',
  primary = 'primary',
  cta = 'cta',
  primaryHp = 'primary-hp',
}

export enum ButtonType {
  button = 'button',
  reset = 'reset',
  submit = 'submit',
}

export enum ButtonVariant {
  contained = 'contained',
  outlined = 'outlined',
  text = 'text',
}

interface SharedComponentProps {
  children: React.ReactNode
  className?: string
  color?: ButtonColor
  disabled?: boolean
  fullWidth?: boolean
  isRounded?: boolean
  onClick?: (args: unknown) => void
  tabIndex?: number
  variant?: ButtonVariant
  testId?: string
}

interface WithButtonProps {
  isLink?: false
  type?: ButtonType
}

interface WithLinkProps {
  isLink: true
  to: string
}

type ButtonProps = SharedComponentProps & (WithButtonProps | WithLinkProps)

const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(function Button(
  props,
  ref,
) {
  const {
    children,
    className,
    color,
    disabled,
    fullWidth,
    isRounded,
    onClick,
    tabIndex,
    variant,
    testId,
  } = props

  const buttonClasses =
    className ||
    classNames('c-button', `c-button--${variant}`, {
      [`c-button--${variant}-${color}`]: color !== ButtonColor.default,
      'c-button--disabled': disabled,
      'c-button--full': fullWidth,
      'c-button--rounded': isRounded,
    })

  const sharedProps = {
    className: buttonClasses,
    onClick,
    tabIndex: disabled ? -1 : tabIndex,
    'data-test-id': testId,
  }

  if (props.isLink) {
    return (
      <AutoLink
        {...sharedProps}
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={props.to}
        aria-disabled={disabled}
        asReactRouterLink
      >
        {children}
      </AutoLink>
    )
  }

  return (
    <button
      {...sharedProps}
      ref={ref as React.Ref<HTMLButtonElement>}
      disabled={disabled}
      type={props.type}
    >
      {children}
    </button>
  )
})

Button.defaultProps = {
  color: ButtonColor.default,
  isRounded: false,
  tabIndex: 0,
  type: ButtonType.button,
  variant: ButtonVariant.contained,
}

export default Button
